import React, { useEffect, useState } from "react";
import { Modal, Form, Button, Dropdown, Input } from "antd";
import { EditOutlined, DeleteOutlined, MoreOutlined } from "@ant-design/icons";

import {
  getInventoryApi,
  addInventoryApi,
  deleteInventoryApi,
  getProductsApi,
  getVendorsApi,
  updateInventoryApi,
} from "../../services/api";

import AppTable from "../../common/AppTable";
import AppSelect from "../../common/FormInputs/AppSelect";
import AppInput from "../../common/FormInputs/AppInput";

export default function Inventory() {
  const [inventory, setInventory] = useState([]);
  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [editingRecord, setEditingRecord] = useState(null);

  const [variants, setVariants] = useState([]);

  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  // ================= FETCH =================
  const fetchInventory = async () => {
    const res = await getInventoryApi();
    console.log("INVENTORY API:", res.data);
    setInventory((Array.isArray(res.data) ? res.data : []).slice().sort((a, b) => b.id - a.id));
  };

  const fetchProducts = async () => {
    const res = await getProductsApi();
    console.log("PRODUCT API:", res.data.data); // 👈 ADD THIS
    setProducts(res.data.data);
  };

  const fetchVendors = async () => {
    const res = await getVendorsApi();
    setVendors(res.data.data);
  };

  useEffect(() => {
    fetchInventory();
    fetchProducts();
    fetchVendors();
  }, []);

  // ================= HANDLE PRODUCT SELECT =================
  const handleProductChange = (productId) => {
    const product = products.find((p) => String(p.id) === String(productId));

    console.log("FOUND PRODUCT:", product);

    setVariants(product?.variants || []);
    form.setFieldValue("variant", undefined);
  };

  // ================= ADD INVENTORY =================
  const handleSubmit = async () => {
    const values = await form.validateFields();

    const payload = {
      product: { id: values.product },
      variant: values.variant ? { id: values.variant } : null,
      vendor: { id: values.vendor },
      quantity: Number(values.quantity),
      batchNo: values.batchNo,
      barcode: values.barcode,
    };

    if (editingRecord) {
      await updateInventoryApi(editingRecord.id, payload); // 🔥 UPDATE
    } else {
      await addInventoryApi(payload); // ➕ CREATE
    }

    setOpen(false);
    setEditingRecord(null);
    form.resetFields();
    fetchInventory();
  };

  // ================= DELETE =================
  const handleDelete = async (id) => {
    await deleteInventoryApi(id);
    fetchInventory();
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setOpen(true);

    // set variants for selected product
    const productId = record.variant?.product?.id || record.product?.id;
    const product = products.find((p) => p.id === productId);

    setVariants(product?.variants || []);

    form.setFieldsValue({
      product: productId,
      variant: record.variant?.id,
      vendor: record.vendor?.id,
      quantity: record.quantity,
      batchNo: record.batchNo,
      barcode: record.barcode,
    });
  };

  // ================= TABLE =================
  const columns = [
    {
      title: "Product",
      render: (r) => r.product?.name || r.variant?.product?.name || "-",
    },
    {
      title: "Variant",
      render: (r) => `${r.variant?.size || ""} / ${r.variant?.color || ""}`,
    },
    {
      title: "Vendor",
      render: (r) => r.vendor?.name || "-",
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
    },
    {
      title: "Batch",
      dataIndex: "batchNo",
    },
    {
      title: "Barcode",
      dataIndex: "barcode",
    },
    {
      title: "Actions",
      width: 70,
      align: "center",
      render: (_, r) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              { key: "edit", label: "Edit", icon: <EditOutlined />, onClick: () => handleEdit(r) },
              { type: "divider" },
              { key: "delete", label: "Delete", icon: <DeleteOutlined />, danger: true, onClick: () => handleDelete(r.id) },
            ],
          }}
        >
          <Button icon={<MoreOutlined />} size="small" />
        </Dropdown>
      ),
    },
  ];

  return (
    <div>
      {/* HEADER */}
      <div className="page-header">
        <div>
          <div className="page-title">Inventory</div>
          <div className="page-sub">{inventory.length} records</div>
        </div>

        <Button type="primary"  style={{
            background: "#534ab7",
            border: "none",
            borderRadius: 8,
          }} onClick={() => setOpen(true)}>
          + Add Stock
        </Button>
      </div>

      {/* TABLE */}
      <AppTable columns={columns} data={inventory} />

      {/* MODAL */}
      <Modal
        title={editingRecord ? "Edit Inventory" : "Add Inventory"}
        open={open}
        onCancel={() => {
          setOpen(false);
          setEditingRecord(null);
          form.resetFields();
        }}
        onOk={handleSubmit}
      >
        <Form form={form} layout="vertical">
          {/* PRODUCT */}
          <AppSelect
            name="product"
            label="Product"
            placeholder="Select product"
            options={products.map((p) => ({
              label: p.name,
              value: p.id,
            }))}
            onChange={(value) => {
              console.log("Selected:", value);
              handleProductChange(value);
            }}
            rules={[{ required: true }]}
          />

          {/* VARIANT */}
          <AppSelect
            name="variant"
            label="Variant"
            placeholder={
              variants.length === 0 ? "No variants available" : "Select variant"
            }
            disabled={variants.length === 0}
            options={variants.map((v) => ({
              label: `${v.size || "-"} / ${v.color || "-"}`,
              value: v.id,
            }))}
          />

          <AppSelect
            name="vendor"
            label="Vendor"
            placeholder="Select vendor"
            options={vendors.map((v) => ({
              label: v.name,
              value: v.id,
            }))}
            rules={[{ required: true }]}
          />

          {/* QUANTITY */}
          <AppInput
            name="quantity"
            label="Quantity"
            type="number"
            rules={[{ required: true }]}
          />

          {/* OPTIONAL */}
          <AppInput name="batchNo" label="Batch No" />
          <AppInput name="barcode" label="Barcode" />
        </Form>
      </Modal>
    </div>
  );
}
